import React, {
  ReactElement,
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef
} from 'react';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import DateInput from '@mui/material/TextField';
import moment from 'moment';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {InputAdornment, Radio, TextField} from '@mui/material';
import {
  Typography,
  RadioGroup,
  FormControlLabel,
  Button,
  Calendar4Icon
} from '@customink/pigment-react';
import {FormSettingsProps, StoreSettings} from 'models/storeSettings';
import {useHistory} from 'react-router';
import Label from '../Label';
import {
  useAppSelector,
  useSettingIsAvailable,
  useStoreType,
  useStoreSlug,
  useGroupOrderTimeline,
  useCampaignStatus
} from '../../hooks';
import {MobileContext} from '../../containers/MobileProvider';
import {
  GroupOrderCancelParamsType,
  ReopenCampaignParamsType,
  useCancelGroupOrderMutation,
  useReopenCampaignMutation,
  useUpdateSettingsMutation
} from '../../api/storesApi';
import {selectPrimaryCampaign} from '../../slices/storesSlice';
import SettingDialog from './Dialog';
import AlertConfirmation from './AlertConfirmation';

interface StoreSettingsError {
  data?: {
    errors?: string;
  };
}

type Option = {
  title: string;
  descrip: string;
  name: keyof StoreSettings;
  radioGroup: any[];
};

const OPTIONS: Option[] = [
  {
    title: 'Delivery Options',
    descrip:
      'You will not be able to change this setting after an order has been placed.',
    name: 'deliveryStrategy',
    radioGroup: [
      {value: 'singleDelivery', label: 'Ship everything to you'},
      {value: 'individualDelivery', label: 'Ship directly to group members'},
      {value: 'choiceDelivery', label: 'Let group members choose'}
    ]
  },
  {
    title: 'Payment Options',
    descrip:
      'You will not be able to change this setting after an order has been placed.',
    name: 'paymentStrategy',
    radioGroup: [
      {value: 'organizerPay', label: 'Pay for the order yourself'},
      {value: 'groupPay', label: 'Have group members pay'}
    ]
  },
  {
    title: 'Order Completion',
    descrip: `We can automatically place your Group Order when your order closes if you've met or exceeded your goal.`,
    name: 'autofulfill',
    radioGroup: [
      {value: 'true', label: 'Turn on auto-complete'},
      {value: 'false', label: 'Manually complete order'}
    ]
  }
];

const GROUP_ORDER_ALERT_OPTIONS = {
  updateSettings: {
    message: 'Your settings have been saved.'
  },
  cancel: {
    message: 'Your Group Order has been canceled.'
  },
  reopen: {
    title: 'Your Order Has Been Re-opened',
    message:
      'Your order has been re-opened and will close in 7 days. To adjust the close date, use the calendar below.'
  }
};

const FormSettings: React.FC<FormSettingsProps> = ({
  storeSlug,
  storeSettings,
  isStoreSettingsLoading,
  storeDetails: {capabilities: storeCapabilities, status: storeStatus}
}: FormSettingsProps): ReactElement => {
  const defaultValues: StoreSettings = {
    name: storeSettings.name,
    urlSlug: storeSettings.urlSlug,
    campaignClosesAt: storeSettings.campaignClosesAt
  };

  const history = useHistory();
  const formMethods = useForm<StoreSettings>({
    defaultValues
  });
  const {
    formState: {
      errors: formErrors,
      isDirty: isFormDirty,
      isSubmitted: isFormSubmitted
    }
  } = formMethods;
  const {settingIsAvailable} = useSettingIsAvailable();
  const {isStoreTypeGroupOrder, getStoreTypeName} = useStoreType();
  const {maxCloseDate, maxCloseDateFormatted} = useGroupOrderTimeline();
  const currentDate = moment();
  const currentDateFormatted = currentDate.format('dddd, MMMM D, YYYY');
  const urlSlug = useStoreSlug();

  const [
    triggerUseUpdateSettingsMutation,
    {isSuccess: isEditSettingsSuccess, error: serverError}
  ] = useUpdateSettingsMutation();
  const [
    triggerCancelGroupOrderMutation,
    {error: cancelGofError, isSuccess: isCancelGoSuccess}
  ] = useCancelGroupOrderMutation();
  const [
    triggerReopenCampaignMutation,
    {error: reopenCampaignError, isSuccess: isReopenCampaignSuccess}
  ] = useReopenCampaignMutation();

  const primaryCampaign = useAppSelector(selectPrimaryCampaign);
  const campaignId = primaryCampaign?.id || '';
  const {isCampaignActive} = useCampaignStatus();
  const canCancel = isCampaignActive;
  const isCampaignReopenable = primaryCampaign?.reopenable;
  const isStoreClosed = storeStatus === 'closed';

  const mobile = useContext(MobileContext);

  const unblockRef = useRef(null);

  const [showGeneralError, setShowGeneralError] = useState(false);
  const [showCancelOrderDialog, setShowCancelOrderDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [closeDatePickerOpen, setCloseDatePickerOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [groupOrderAction, setGroupOrderAction] = useState<
    'cancel' | 'reopen' | 'updateSettings'
  >('updateSettings');

  /* 
    Form handlers
  */

  const handleSubmit = (data: StoreSettings) => {
    const settingsParams: StoreSettings = {
      name: data.name,
      urlSlug: data.urlSlug,
      campaignClosesAt: data.campaignClosesAt
    };
    triggerUseUpdateSettingsMutation({
      details: settingsParams,
      slug: storeSlug
    });
  };

  const handleSlugOnChange = (enteredSlug: string) => {
    return enteredSlug.replace(/[^a-zA-Z0-9-]/g, '');
  };

  const preventDateTyping = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDialogClose = () => {
    setShowCancelOrderDialog(false);
  };

  const handleCancelDialogConfirm = () => {
    const params = {
      url_slug: urlSlug,
      id: campaignId
    } as GroupOrderCancelParamsType;

    triggerCancelGroupOrderMutation(params);
    return handleDialogClose();
  };

  const handleLeaveDialogConfirm = () => {
    if (unblockRef) {
      unblockRef.current();
    }
    setShowLeaveDialog(false);
    history.push(currentPath);
  };

  const handleUpdateSettingsSuccess = () => {
    if (isEditSettingsSuccess) {
      const slug = formMethods.getValues('urlSlug');
      if (storeSlug !== slug) {
        history.push(`/${slug}/manage/settings`);
      }

      setShowSuccessAlert(true);
    }
  };

  const handleUpdateSettingsFailure = () => {
    if (serverError) {
      if (
        (serverError as StoreSettingsError).data?.errors &&
        (serverError as StoreSettingsError).data?.errors.includes(
          'Validation failed: Url slug has already been taken'
        )
      ) {
        formMethods.setError('urlSlug', {
          type: 'server',
          message: 'That URL is already taken. Please try a different URL.'
        });
      } else {
        setShowGeneralError(true);
      }
    } else {
      setShowGeneralError(false);
    }
  };

  const handleReopenCampaign = useCallback(() => {
    const params = {
      storeSlug: urlSlug,
      campaignId
    } as ReopenCampaignParamsType;
    triggerReopenCampaignMutation(params);
  }, [urlSlug, campaignId]);

  const handleCloseAlert = () => setShowSuccessAlert(false);
  const handleOpenAlert = () => setShowSuccessAlert(true);

  /* 
    useEffect hooks
  */

  // Set form default values when settings load
  useEffect(() => {
    if (!isStoreSettingsLoading) {
      const newValues = {
        ...defaultValues,
        name: storeSettings?.name,
        urlSlug: storeSlug,
        campaignClosesAt: storeSettings?.campaignClosesAt,
        autofulfill: storeSettings?.autofulfill?.toString()
      };
      formMethods.reset({...newValues});
    }
  }, [storeSettings]);

  useEffect(() => {
    if (cancelGofError) {
      formMethods.setError('campaignClosesAt', {
        type: 'server',
        message: 'Something went wrong canceling your Group Order'
      });
    }
  }, [cancelGofError]);

  useEffect(() => {
    if (isCancelGoSuccess) {
      setGroupOrderAction('cancel');
      handleOpenAlert();
    }
  }, [isCancelGoSuccess]);

  useEffect(() => {
    if (reopenCampaignError) {
      formMethods.setError('campaignClosesAt', {
        type: 'server',
        message: 'Something went wrong reopening your Group Order'
      });
    }
  }, [reopenCampaignError]);

  useEffect(() => {
    if (isReopenCampaignSuccess) {
      setGroupOrderAction('reopen');
      handleOpenAlert();
    }
  }, [isReopenCampaignSuccess]);

  useEffect(() => {
    unblockRef.current = history.block((location: typeof history) => {
      if (isFormDirty && !isFormSubmitted) {
        setCurrentPath(location.pathname);

        setShowLeaveDialog(true);
        return false;
      }
      return true;
    });
    return () => unblockRef.current && unblockRef.current();
  }, [formMethods.formState, isFormDirty]);

  useEffect(handleUpdateSettingsFailure, [serverError]);
  useEffect(handleUpdateSettingsSuccess, [isEditSettingsSuccess]);

  const renderCloseDate = () => {
    return (
      <div className="form-field-item setting-section">
        <div className="tab-subheading">
          <Typography variant="subHeading">Close Date</Typography>
        </div>
        <div className="sub-title-descrip">
          <Typography variant="bodyShort2">
            Group members must place their orders by 11:59pm ET on this day.
            Place your order within 24 hours of your close date to avoid delays.
          </Typography>
        </div>
        <Label required>This Order Will Close On:</Label>
        <div className="closeDate">
          <Controller
            render={({field}) => (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  open={closeDatePickerOpen}
                  onClose={() => setCloseDatePickerOpen(false)}
                  components={{
                    OpenPickerIcon: Calendar4Icon
                  }}
                  InputAdornmentProps={{
                    position: 'start'
                  }}
                  onChange={field.onChange}
                  inputRef={field.ref}
                  value={field.value}
                  minDate={currentDate}
                  maxDate={maxCloseDate}
                  inputFormat={
                    mobile
                      ? 'ddd[.,] MMM[.] D[,] YYYY'
                      : 'dddd[,] MMMM D[,] YYYY'
                  }
                  disablePast
                  disableHighlightToday
                  disableMaskedInput
                  disabled={isStoreClosed}
                  renderInput={params => (
                    <DateInput
                      required
                      onClick={() => setCloseDatePickerOpen(true)}
                      onKeyDown={preventDateTyping}
                      {...params}
                      error={!!formErrors.campaignClosesAt}
                    />
                  )}
                />
              </LocalizationProvider>
            )}
            name="campaignClosesAt"
            control={formMethods.control}
            rules={{
              required: true,
              validate: date =>
                (moment(date).isSameOrAfter(currentDate) &&
                  moment(date).isBefore(maxCloseDate)) ||
                `Enter a date between ${currentDateFormatted} and ${maxCloseDateFormatted}.`
            }}
          />
          {isCampaignReopenable ? (
            <Button
              className="GroupOrderReopenBtn"
              variant="secondary"
              onClick={handleReopenCampaign}
              size="small">
              {mobile ? 'Re-open' : 'Re-open Order'}
            </Button>
          ) : null}

          {storeCapabilities?.includes('cancelCampaign') ? (
            <Button
              className={`GroupOrderCancelBtn ${canCancel ? '' : 'disable'}`}
              variant="link"
              size="small"
              disabled={!canCancel}
              onClick={() => {
                setShowCancelOrderDialog(true);
              }}>
              Cancel Group Order
            </Button>
          ) : null}
        </div>
        <div className="closeDateHelpText">
          <Typography variant="captionRegular">
            Your close date must be within 90 days of your {!mobile && <br />}
            launch date.
          </Typography>
        </div>
        <div className="closeDateError">
          {formErrors.campaignClosesAt && (
            <span className="error" role="alert">
              {formErrors.campaignClosesAt?.message}
            </span>
          )}
        </div>
        {/* TO DO: populate shipping calculator */}
        {/* <div className="inputHelper">
          <Typography>
            Need your order by a specific date? Use our
            <br /> <a href="/">shipping calculator.</a>{' '}
          </Typography>
        </div> */}
      </div>
    );
  };

  const renderOptionsList = () => {
    return (
      <>
        {OPTIONS.map(item => {
          return settingIsAvailable(item.name) ? (
            <div key={item.name} className="form-field-item">
              <div className="tab-subheading required">
                <Typography variant="subHeading">{item.title}</Typography>
              </div>
              <div>
                <Typography variant="bodyShort2">{item.descrip}</Typography>
              </div>
              <Controller
                render={({field}) => (
                  <RadioGroup {...field} row>
                    {item.radioGroup.map(radio => {
                      return (
                        <FormControlLabel
                          className="radioOptionRow"
                          key={radio.label}
                          value={radio.value}
                          control={<Radio color="info" />}
                          label={radio.label}
                        />
                      );
                    })}
                  </RadioGroup>
                )}
                name={item.name}
                control={formMethods.control}
              />
            </div>
          ) : null;
        })}
      </>
    );
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(handleSubmit)} className="form">
        {showGeneralError && (
          <Typography variant="subHeading" className="generalError">
            Something went wrong. Please try again later.
          </Typography>
        )}
        <div className="formSettings">
          <div className="form-field">
            <div className="form-field-item setting-section">
              <div className="tab-subheading">
                <Typography variant="subHeading">Page Details</Typography>
              </div>
              <div className="sub-title-descrip">
                <Typography variant="bodyShort2">
                  {`Your ${getStoreTypeName} name and URL will be visible to your group.`}
                </Typography>
              </div>
              <Label required>{getStoreTypeName} Name</Label>
              <Controller
                render={({field}) => (
                  <TextField
                    error={!!formErrors.name}
                    value={field.value}
                    onChange={field.onChange}
                    inputRef={field.ref}
                    helperText={
                      formErrors.name && (
                        <span className="error" role="alert">
                          {formErrors.name?.message}
                        </span>
                      )
                    }
                  />
                )}
                name="name"
                control={formMethods.control}
                rules={{
                  required: 'Name is required.',
                  maxLength: {
                    value: 100,
                    message: 'Name cannot be more than 100 characters.'
                  }
                }}
              />
              <Label required>{getStoreTypeName} URL</Label>
              <Controller
                render={({field}) => (
                  <TextField
                    required
                    error={!!formErrors.urlSlug}
                    onChange={e => {
                      field.onChange(handleSlugOnChange(e.target.value));
                    }}
                    inputRef={field.ref}
                    value={field.value}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {isStoreTypeGroupOrder
                            ? 'customink.com/go/'
                            : 'customink.com/s/'}
                        </InputAdornment>
                      )
                    }}
                    helperText={
                      formErrors.urlSlug && (
                        <span className="error" role="alert">
                          {formErrors.urlSlug?.message}
                        </span>
                      )
                    }
                  />
                )}
                name="urlSlug"
                control={formMethods.control}
                rules={{
                  required: 'URL is required.',
                  maxLength: {
                    value: 100,
                    message: 'URL cannot be more than 100 characters.'
                  }
                }}
              />
            </div>

            <div>
              {storeCapabilities?.includes('setCloseDate')
                ? renderCloseDate()
                : null}
              {renderOptionsList()}
            </div>
          </div>
          <div className="settingsActions">
            <Button
              className="cancel-button"
              aria-label="reset"
              variant="link"
              disabled={!isFormDirty}
              onClick={() => {
                setShowGeneralError(false);
                formMethods.reset();
              }}>
              Cancel
            </Button>
            <Button
              className="submit-button"
              type="submit"
              disabled={!isFormDirty}>
              Save
            </Button>
          </div>
        </div>
      </form>
      <SettingDialog
        open={showLeaveDialog}
        title="Are you sure you want to leave without saving?"
        alertContent="You have unsaved changes. If you continue without saving, your recent changes will be lost."
        closeText="Go Back"
        confirmText="Continue Without Saving"
        handleConfirm={handleLeaveDialogConfirm}
        handleClose={() => setShowLeaveDialog(false)}
      />
      <SettingDialog
        open={showCancelOrderDialog}
        title="Cancel Your Group Order"
        alertContent="All orders will be canceled and your Group Order will be removed."
        descriptionContent="Any payments you collected online will be refunded automatically to your group members."
        confirmText="Cancel Order"
        handleConfirm={handleCancelDialogConfirm}
        handleClose={handleDialogClose}
      />
      <AlertConfirmation
        showSuccessAlert={showSuccessAlert}
        onCloseAlert={handleCloseAlert}
        groupOrderAlert={GROUP_ORDER_ALERT_OPTIONS[groupOrderAction]}
      />
    </FormProvider>
  );
};

export default FormSettings;
